import { FieldTemplate, LayoutItem } from 'api/models';
import { Field } from 'api/models/Form';
import { produce } from 'immer';
import { insert, get } from 'object-path';
import { getId } from 'utils';

function getValueType(fieldTemplate?: FieldTemplate) {
  switch (fieldTemplate?.fieldType) {
    case 'number':
      return 'number';
    case 'select':
      return fieldTemplate.fieldAttributes.isMultiSelect
        ? 'stringlist'
        : 'string';

    case 'date':
    default:
      return 'string';
  }
}

function getValue(value: any, valueType?: string) {
  switch (valueType) {
    case 'number':
      return parseFloat(value);
    case 'string':
    default:
      return value;
  }
}

export function getFields(
  values: {
    [key: string]: string | number | boolean | string[] | Date | null;
  },
  fieldTemplates: FieldTemplate[],
  layoutItems: LayoutItem[],
  path?: string,
): Field[] {
  // the list needs to be flattened because the children are nested because of the recursive call
  const fields = layoutItems
    .flatMap((layoutItem, itemIndex) => {
      // since we have layout fields with nested children, we need to recursively call this function
      if (layoutItem.children && layoutItem.children.length)
        return getFields(
          values,
          fieldTemplates,
          layoutItem.children,
          `${itemIndex}.children`,
        );
      let fieldId = '';
      let repeatIndex = 0;
      let layoutRepeatIndex: number[] = [];
      const fieldTemplate = fieldTemplates.find(({ id }) => {
        if (!layoutItem.fieldTemplateId) return false;
        fieldId = layoutItem.fieldTemplateId ?? id;
        const [fId, rIndex] =
          (layoutItem.fieldTemplateId ?? id).split(':') ?? [];

        const [layoutFieldId] =
          (layoutItem.fieldTemplateId ?? id).split('|') ?? [];

        if (layoutFieldId) {
          layoutRepeatIndex = layoutItem.fieldTemplateId
            .split('|')
            .filter((_id, ind) => ind > 0)
            .map(i => parseInt(i));
        }
        repeatIndex = parseInt(rIndex ?? 0);
        return id === fId || layoutFieldId === id;
      });

      if (!fieldTemplate) return null;
      const valueType = getValueType(fieldTemplate);
      let value = values[fieldId]
        ? getValue(values[fieldId], valueType)
        : getValue(fieldTemplate.fieldAttributes.defaultValue, valueType);

      if (!values[fieldId]) {
        if (values[fieldId] !== null) {
          value = '';
        } else {
          // null means that the value was deleted by the user
          value = null;
        }
      }

      const field = {
        fieldType: fieldTemplate?.fieldType ?? '',
        valueType,
        fieldTemplateId: fieldTemplate?.id ?? '',
        value,
        path: path ? `${path}.${itemIndex}.children` : `${itemIndex}`,
      };
      if (fieldTemplate.fieldAttributes.isRepeatable)
        // @ts-ignore
        field.repeatIndex = repeatIndex;

      if (layoutRepeatIndex.length > 0) {
        // TODO: once backend supports it get rid of .join('|')
        // @ts-ignore
        field.layoutRepeatIndex = layoutRepeatIndex.join('|');
      }
      return field;
    })
    .filter(Boolean) as Field[];
  return fields;
}

export function getWidthFromXs(
  xsValue?: string,
  containerWidth?: number,
): number {
  if (xsValue === undefined) return 100;
  if (containerWidth === undefined) return 100;
  const value = parseInt(xsValue);
  if (value === 12) return containerWidth;
  if (value === 11) return containerWidth * 0.9166666666666666;
  if (value === 10) return containerWidth * 0.8333333333333334;
  if (value === 9) return containerWidth * 0.75;
  if (value === 8) return containerWidth * 0.6666666666666666;
  if (value === 7) return containerWidth * 0.5833333333333336;
  if (value === 6) return containerWidth * 0.5;
  if (value === 5) return containerWidth * 0.4166666666666667;
  if (value === 4) return containerWidth * 0.3333333333333333;
  if (value === 3) return containerWidth * 0.25;
  if (value === 2) return containerWidth * 0.16666666666666664;
  if (value === 1) return containerWidth * 0.08333333333333332;

  return containerWidth;
}

export function getXsFromWidth({
  resizeWidth,
  rowWidth,
}: {
  resizeWidth?: number;
  rowWidth: number;
}) {
  const width = resizeWidth ?? rowWidth;
  const ratio = width / rowWidth;
  const gridXSValue = Math.round(12 * ratio);

  if (gridXSValue > 12) return '12';
  if (gridXSValue < 1) return '1';

  const stringXSValue = isNaN(gridXSValue) ? '12' : gridXSValue.toString();
  return stringXSValue ?? '12';
}

export function getRemainingSpace(columns?: LayoutItem[]) {
  const columnsSpace =
    columns?.reduce((acc: number, column: LayoutItem) => {
      return acc + parseInt(column.layoutOptions?.layout ?? '12');
    }, 0) ?? 0;
  const newColumnWidth = 12 - columnsSpace;
  return newColumnWidth.toString();
}

export function insertItemsForRepeatingFields(
  fields: Field[],
  items: LayoutItem[],
): LayoutItem[] {
  fields
    .filter(
      f => (f.repeatIndex ?? -1) >= 1 && f.layoutRepeatIndex === undefined,
    )
    .sort((a, b) => {
      if (a.fieldTemplateId === b.fieldTemplateId) {
        return b.repeatIndex! - a.repeatIndex!;
      }
      return 0;
    })
    .forEach(f => {
      const { path, index, item } = getItemAndIndex(
        items,
        '',
        f.fieldTemplateId,
      );

      const updatedItems = produce(items, draft => {
        insert(
          draft,
          path,
          {
            ...(item ?? {}),
            id: getId(),
            fieldTemplateId: `${
              (item ?? ({} as LayoutItem))?.fieldTemplateId
            }:${f.repeatIndex}`,
          },
          index + 1,
        );
      });
      items = updatedItems;
    });

  return items;
}

export function insertLayoutItemsForRepeatingFields(
  fields: Field[],
  items: LayoutItem[],
): LayoutItem[] {
  let updatedItems = [...items];
  fields
    .filter(f => (f.layoutRepeatIndex?.length ?? -1) >= 1)
    .sort((a, b) => a.layoutRepeatIndex![0] - b.layoutRepeatIndex![0])
    .forEach(f => {
      const res = findRepeatableLayoutItem(items, f.fieldTemplateId);
      if (res) {
        const { item, path, index } = res;
        const updatedItem = updateLayoutRepeatIndexForInputLayoutItems(
          item,
          (f.layoutRepeatIndex ?? [])[0],
        );

        const localPath =
          path.split('.').length === 1
            ? ''
            : path.split('.').slice(0, -1).join('.');

        // TODO: update only last index of path not the whole path, it will keep breaking for nested items
        if (
          // @ts-ignore
          get(updatedItems, parseInt(path) + parseInt(f.layoutRepeatIndex![0]))
            ?.layoutOptions?.repeatIndex !== f.layoutRepeatIndex![0]
        ) {
          insert(
            updatedItems,
            localPath,
            {
              ...updatedItem,
              id: getId(),
              layoutOptions: {
                ...updatedItem.layoutOptions,
                repeatIndex: f.layoutRepeatIndex![0],
                replicaOf: item.id,
              },
            },
            index + f.layoutRepeatIndex![0],
          );
          // });
        }
      }
    });

  return updatedItems;
}

function getItemAndIndex(
  layoutItems: LayoutItem[],
  path: string,
  fieldTemplateId: string,
) {
  let insertIndex = -1;
  let foundItem: LayoutItem | null = null;
  let correctPath = '';

  layoutItems.forEach((item, index) => {
    if (item.fieldTemplateId === fieldTemplateId) {
      foundItem = item;
      insertIndex = index;
      correctPath = path;
    }

    if (item.children) {
      const res = getItemAndIndex(
        item.children,
        `${path ? `${path}.` : ''}${index}.children`,
        fieldTemplateId,
      );

      if (res.item) {
        insertIndex = res.index;
        foundItem = res.item;
        correctPath = res.path;
      }
    }
  });

  return {
    index: insertIndex,
    item: foundItem,
    path: correctPath,
  };
}

export function duplicateLayoutItem(
  item: LayoutItem,
  layoutRepeatIndex: number,
): LayoutItem {
  return {
    ...item,
    id: getId(),
    children: item.children
      ? item.children.map(i => duplicateLayoutItem(i, layoutRepeatIndex))
      : undefined,
    fieldTemplateId: item.fieldTemplateId
      ? `${item.fieldTemplateId.split('|')[0]}|${layoutRepeatIndex}`
      : undefined,
    layoutOptions: {
      ...item.layoutOptions,
      replicaOf: item.id,
    },
  } as LayoutItem;
}

export function getFieldTemplateIdsOfLayoutField(item: LayoutItem): string[] {
  if (item.fieldTemplateId) return [item.fieldTemplateId];

  if (item.children) {
    return item.children.flatMap(i => getFieldTemplateIdsOfLayoutField(i));
  }

  return [];
}

function findRepeatableLayoutItem(
  layoutItems: LayoutItem[],
  fieldTemplateId: string,
) {
  // Helper function to check if an item is repeatable
  function isRepeatable(item: LayoutItem) {
    return item.layoutOptions && item.layoutOptions.isRepeatable === true;
  }

  // Helper function to search for fieldTemplateId recursively
  function searchRecursively(
    items: LayoutItem[],
    currentPath = '',
  ): null | {
    item: LayoutItem;
    path: string;
    index: number;
  } {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const itemPath = currentPath ? `${currentPath}.${i}` : `${i}`;

      // Check if the current item is repeatable and contains the fieldTemplateId
      if (
        isRepeatable(item) &&
        containsFieldTemplateId(item, fieldTemplateId)
      ) {
        return { item, path: itemPath, index: i };
      }

      // If the item has children, search recursively
      if (item.children && item.children.length > 0) {
        const result = searchRecursively(item.children, `${itemPath}.children`);
        if (result) return result;
      }
    }
    return null;
  }

  // Helper function to check if an item or its descendants contain the fieldTemplateId
  function containsFieldTemplateId(
    item: LayoutItem,
    targetId: string,
  ): boolean {
    if (item.fieldTemplateId === targetId) return true;
    if (item.children && item.children.length > 0) {
      return item.children.some(child =>
        containsFieldTemplateId(child, targetId),
      );
    }
    return false;
  }

  // Start the recursive search
  return searchRecursively(layoutItems);
}

// recursively checks how many layout items are present in the array of layout items with the same replicaId
export function getLayoutRepeatIndex(item: LayoutItem[], replicaId?: string) {
  let count = 0;
  item.forEach(i => {
    if (i.layoutOptions?.replicaOf === replicaId) {
      count++;
    }
    if (i.children) {
      count += getLayoutRepeatIndex(i.children, replicaId);
    }
  });
  return count;
}

// recursively updates fieldtemplateId of layout items with `fieldTempalted:layoutRepeatIndex` format
function updateLayoutRepeatIndexForInputLayoutItems(
  item: LayoutItem,
  layoutRepeatIndex: number,
) {
  return produce(item, draft => {
    if (draft.fieldTemplateId) {
      draft.fieldTemplateId = `${
        draft.fieldTemplateId.split('|')[0]
      }|${layoutRepeatIndex}`;
    }
    if (draft.children) {
      draft.children = draft.children.map(i =>
        updateLayoutRepeatIndexForInputLayoutItems(i, layoutRepeatIndex),
      );
    }
  });
}

export function isRepeatButtonVisible(
  items: LayoutItem[],
  item: LayoutItem,
  field: FieldTemplate,
) {
  const fieldTemplateId = item.fieldTemplateId?.split(':')[0] ?? '';
  const repeatIndex = item.fieldTemplateId?.split(':')[1] ?? '0';

  const repeatableItems = items.filter(
    i => i.fieldTemplateId?.split(':')[0] === fieldTemplateId,
  );

  if (repeatableItems.length === 0) return false;

  const highestRepeatIndex = Math.max(
    ...repeatableItems.map(i =>
      parseInt(i.fieldTemplateId?.split(':')[1] ?? '0'),
    ),
  );

  return (
    field.fieldAttributes.isRepeatable &&
    item.fieldTemplateId &&
    highestRepeatIndex === parseInt(repeatIndex)
  );
}

export function insertItemsForRepeatingFieldsInRepeatingLayoutItems(
  fields: Field[],
  items: LayoutItem[],
): LayoutItem[] {
  let updatedItems = [...items];
  fields
    .filter(
      f =>
        (f.layoutRepeatIndex?.length ?? -1) >= 1 && (f.repeatIndex ?? -1 > 0),
    )
    // sort based on layoutRepeatIndex and then on repeatIndex
    .sort((a, b) => {
      if (a.layoutRepeatIndex![0] === b.layoutRepeatIndex![0]) {
        return b.repeatIndex! - a.repeatIndex!;
      }
      return a.layoutRepeatIndex![0] - b.layoutRepeatIndex![0];
    })
    .forEach(f => {
      const { path } =
        findRepeatableLayoutItem(updatedItems, f.fieldTemplateId) ?? {};

      const p = getPathForLayoutRepeatIndexAndRepeatIndex(
        path,
        f.layoutRepeatIndex![0],
      );

      const foundItem = get(items, `${p}.children`);

      const {
        path: insertIntoPath,
        index,
        item,
      } = getItemAndIndex(
        foundItem,
        `${p}.children`,
        `${f.fieldTemplateId}|${f.layoutRepeatIndex![0]}`,
      );

      const newItems = produce(items, draft => {
        insert(
          draft,
          insertIntoPath,
          {
            ...(item ?? {}),
            id: getId(),
            fieldTemplateId: `${f.fieldTemplateId}:${f.repeatIndex}|${
              f.layoutRepeatIndex![0]
            }`,
          },
          index + 1,
        );
      });
      updatedItems = newItems;
    });
  return updatedItems;
}

function getPathForLayoutRepeatIndexAndRepeatIndex(
  path: string = '',
  layoutRepeatIndex: number,
) {
  const pathArr = path.split('.');
  const lastPathIndex = parseInt(pathArr[pathArr.length - 1]);
  pathArr[pathArr.length - 1] = `${lastPathIndex + layoutRepeatIndex}`;
  return pathArr.join('.');
}
